import {
  AlertCircle,
  ArrowRight,
  Calendar,
  CheckCircle2,
  ChevronDown,
  Clock,
  Download,
  FileText,
  RefreshCw,
  ShieldAlert,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import DataTable, { type Column } from "@/components/ui/DataTable";
import { CardSkeleton, SkeletonBlock, TableSkeleton } from "@/components/ui/Skeleton";
import StatCard from "@/components/ui/StatCard";
import StatusBadge, { STATUS_META } from "@/components/ui/StatusBadge";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import type { MandateStatus } from "@/types/mandate";

import { WALLET_RATES } from "@/pages/retailer/wallet/constants";
import DonutChart, { type DonutSegment } from "@/pages/retailer/dashboard/components/DonutChart";
import AdminLineChart, { type AdminSeriesPoint } from "./components/AdminLineChart";
import { formatDate, formatINR, formatTime, MONTHS, RANGES, relativeTime } from "@/pages/admin/data";
import { adminService, kycService, mandateService, walletService } from "@/services/apiServices";

function successBarColor(rate: number): string {
  if (rate > 75) return "#059669";
  if (rate >= 60) return "#D97706";
  return "#DC2626";
}

function initials(name: string): string {
  if (!name) return "RT";
  return name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

interface DashboardMetrics {
  totalMandates: number;
  activeMandates: number;
  pendingAuthMandates: number;
  completedMandates: number;
  failedRejectedMandates: number;
  failedRejectedPct: string;
  successRate: string;
  totalRetailers: number;
  activeRetailers: number;
  mandatesToday: number;
  authorisedToday: number;
  walletBalance: number;
  blockedRetailersCount: number;
  pendingKyc: number;
  totalMandatesTrend?: number;
  activeMandatesTrend?: number;
  successRateTrend?: number;
}

interface DashboardTrendSeries {
  created: AdminSeriesPoint[];
  activated: AdminSeriesPoint[];
}

interface DashboardTopRetailer {
  id: string;
  name: string;
  shopName: string;
  city: string;
  total: number;
  active: number;
  successRate: number;
  lastLoginAt: string | null;
  isActive: boolean;
}

interface DashboardRecentMandate {
  id: string;
  refNo: string;
  retailerId: string;
  shopName: string;
  customerName: string;
  customerMobile: string;
  amount: number;
  frequency: string;
  status: MandateStatus;
  createdAt: string;
}

interface DashboardData {
  metrics: DashboardMetrics;
  trends: {
    daily: DashboardTrendSeries;
    monthly: DashboardTrendSeries;
  };
  statusBreakdown: {
    label: MandateStatus;
    value: number;
    color: string;
    percentage: number;
  }[];
  topRetailers: DashboardTopRetailer[];
  recentMandates: DashboardRecentMandate[];
}

export default function AdminDashboard() {
  return (
    <ToastProvider>
      <DashboardInner />
    </ToastProvider>
  );
}

function DashboardInner() {
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [range, setRange] = useState("Last 30 Days");
  const [rangeOpen, setRangeOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [showCustomModal, setShowCustomModal] = useState(false);

  const [trendMode, setTrendMode] = useState<"Daily" | "Monthly">("Daily");
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);

  const fetchDashboard = useCallback(
    async (selectedRange = range, from = customFrom, to = customTo, isRefresh = false) => {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        // Parallel fetch of dashboard endpoint and supporting datasets for seamless live aggregation
        const [dashRes, mandatesRes, retailersRes, walletRes, kycRes] = await Promise.allSettled([
          adminService.getDashboard({
            range: selectedRange,
            customFrom: from || undefined,
            customTo: to || undefined,
          }),
          mandateService.getMandates({ limit: 1000 }),
          adminService.getRetailers({ limit: 1000 }),
          walletService.getAdminSummary(),
          kycService.getAdminList(),
        ]);

        const rawDash = dashRes.status === "fulfilled" ? dashRes.value?.data : null;

        // Live mandates source
        const liveMandates: any[] =
          mandatesRes.status === "fulfilled" && Array.isArray(mandatesRes.value?.data?.mandates)
            ? mandatesRes.value.data.mandates
            : rawDash?.recentMandates && Array.isArray(rawDash.recentMandates)
            ? rawDash.recentMandates
            : [];

        // Live retailers source
        const liveRetailers: any[] =
          retailersRes.status === "fulfilled" && Array.isArray(retailersRes.value?.data?.retailers)
            ? retailersRes.value.data.retailers
            : rawDash?.topRetailers && Array.isArray(rawDash.topRetailers)
            ? rawDash.topRetailers
            : [];

        // Live wallets source
        const liveWallets: any[] =
          walletRes.status === "fulfilled" && Array.isArray(walletRes.value?.data?.wallets)
            ? walletRes.value.data.wallets
            : [];

        // Live KYC source
        const liveKyc: any[] =
          kycRes.status === "fulfilled" && Array.isArray(kycRes.value?.data?.records)
            ? kycRes.value.data.records
            : [];

        // Base metrics resolution
        const backendMetrics = rawDash?.metrics || {};

        const totalMandates = Number(
          backendMetrics.totalMandates !== undefined && backendMetrics.totalMandates !== null
            ? backendMetrics.totalMandates
            : liveMandates.length
        );

        const activeMandates = Number(
          backendMetrics.activeMandates !== undefined && backendMetrics.activeMandates !== null
            ? backendMetrics.activeMandates
            : liveMandates.filter((m) => m.status === "ACTIVE").length
        );

        const pendingAuthMandates = Number(
          backendMetrics.pendingAuthMandates !== undefined && backendMetrics.pendingAuthMandates !== null
            ? backendMetrics.pendingAuthMandates
            : liveMandates.filter((m) => m.status === "PENDING_AUTH").length
        );

        const completedMandates = Number(
          backendMetrics.completedMandates !== undefined && backendMetrics.completedMandates !== null
            ? backendMetrics.completedMandates
            : liveMandates.filter((m) => m.status === "COMPLETED").length
        );

        const failedRejectedMandates = Number(
          backendMetrics.failedRejectedMandates !== undefined && backendMetrics.failedRejectedMandates !== null
            ? backendMetrics.failedRejectedMandates
            : liveMandates.filter((m) => m.status === "TERMINATED" || m.status === "HALTED").length
        );

        const successRate =
          backendMetrics.successRate !== undefined && backendMetrics.successRate !== null
            ? String(backendMetrics.successRate)
            : totalMandates > 0
            ? ((activeMandates / totalMandates) * 100).toFixed(1)
            : "0.0";

        const failedRejectedPct =
          backendMetrics.failedRejectedPct !== undefined && backendMetrics.failedRejectedPct !== null
            ? String(backendMetrics.failedRejectedPct)
            : totalMandates > 0
            ? ((failedRejectedMandates / totalMandates) * 100).toFixed(1)
            : "0.0";

        const totalRetailers = Number(
          backendMetrics.totalRetailers !== undefined && backendMetrics.totalRetailers !== null
            ? backendMetrics.totalRetailers
            : liveRetailers.length
        );

        const activeRetailers = Number(
          backendMetrics.activeRetailers !== undefined && backendMetrics.activeRetailers !== null
            ? backendMetrics.activeRetailers
            : liveRetailers.filter((r) => r.isActive !== false).length
        );

        // Calculate today's activity
        const now = new Date();
        const nowDay = now.getDate();
        const nowMonth = now.getMonth();
        const nowYear = now.getFullYear();

        const todayMatches = liveMandates.filter((m) => {
          const raw = m.rawCreatedAt || m.createdAt;
          if (!raw) return false;
          const d = new Date(raw);
          return d.getDate() === nowDay && d.getMonth() === nowMonth && d.getFullYear() === nowYear;
        });

        const mandatesToday = Number(
          backendMetrics.mandatesToday !== undefined && backendMetrics.mandatesToday !== null
            ? backendMetrics.mandatesToday
            : todayMatches.length
        );

        const authorisedToday = Number(
          backendMetrics.authorisedToday !== undefined && backendMetrics.authorisedToday !== null
            ? backendMetrics.authorisedToday
            : todayMatches.filter((m) => m.status === "ACTIVE").length
        );

        // Wallet Balance resolution
        let walletBalance = Number(backendMetrics.walletBalance);
        if (!Number.isFinite(walletBalance)) {
          const wSum = liveWallets.reduce((acc, w) => acc + Number(w.totalBalance || w.balance || 0), 0);
          walletBalance =
            wSum > 0
              ? wSum
              : liveRetailers.reduce((acc, r) => acc + Number(r.walletBalance || 0), 0);
        }

        const blockedRetailersCount = Number(
          backendMetrics.blockedRetailersCount !== undefined && backendMetrics.blockedRetailersCount !== null
            ? backendMetrics.blockedRetailersCount
            : liveWallets.length > 0
            ? liveWallets.filter((w) => Number(w.totalBalance || w.balance || 0) < WALLET_RATES.MIN_BALANCE).length
            : liveRetailers.filter((r) => Number(r.walletBalance || 0) < WALLET_RATES.MIN_BALANCE).length
        );

        // Pending KYC resolution
        const pendingKyc = Number(
          backendMetrics.pendingKyc !== undefined && backendMetrics.pendingKyc !== null
            ? backendMetrics.pendingKyc
            : liveKyc.filter((k) => k.status === "SUBMITTED" || k.status === "UNDER_REVIEW" || k.status === "PENDING").length
        );

        // Dynamic Line Chart Trend Points
        let dailyCreated = rawDash?.trends?.daily?.created;
        let dailyActivated = rawDash?.trends?.daily?.activated;

        if (!Array.isArray(dailyCreated) || dailyCreated.length === 0) {
          dailyCreated = [];
          dailyActivated = [];
          for (let i = 29; i >= 0; i--) {
            const d = new Date(now);
            d.setDate(now.getDate() - i);
            const dDay = d.getDate();
            const dMonth = d.getMonth();
            const dYear = d.getFullYear();
            const label = `${dDay} ${MONTHS[dMonth]}`;

            let c = 0;
            let a = 0;
            liveMandates.forEach((m) => {
              const raw = m.rawCreatedAt || m.createdAt;
              if (raw) {
                const md = new Date(raw);
                if (md.getDate() === dDay && md.getMonth() === dMonth && md.getFullYear() === dYear) {
                  c += 1;
                  if (m.status === "ACTIVE") a += 1;
                }
              }
            });
            dailyCreated.push({ label, value: c });
            dailyActivated.push({ label, value: a });
          }
        }

        let monthlyCreated = rawDash?.trends?.monthly?.created;
        let monthlyActivated = rawDash?.trends?.monthly?.activated;

        if (!Array.isArray(monthlyCreated) || monthlyCreated.length === 0) {
          const monthsMap = new Map<number, { created: number; active: number }>();
          for (let i = 0; i < 12; i++) {
            monthsMap.set(i, { created: 0, active: 0 });
          }
          liveMandates.forEach((m) => {
            const raw = m.rawCreatedAt || m.createdAt;
            if (raw) {
              const md = new Date(raw);
              const mo = md.getMonth();
              const cur = monthsMap.get(mo) || { created: 0, active: 0 };
              cur.created += 1;
              if (m.status === "ACTIVE") cur.active += 1;
              monthsMap.set(mo, cur);
            }
          });
          monthlyCreated = [];
          monthlyActivated = [];
          for (let i = 0; i < 12; i++) {
            const cur = monthsMap.get(i) || { created: 0, active: 0 };
            monthlyCreated.push({ label: MONTHS[i], value: cur.created });
            monthlyActivated.push({ label: MONTHS[i], value: cur.active });
          }
        }

        // Donut Chart Status Breakdown
        let statusBreakdown = rawDash?.statusBreakdown;
        if (!Array.isArray(statusBreakdown) || statusBreakdown.length === 0) {
          const counts = new Map<string, number>();
          liveMandates.forEach((m) => {
            const st = m.status || "CREATED";
            counts.set(st, (counts.get(st) || 0) + 1);
          });
          const DONUT_STATUSES: MandateStatus[] = [
            "ACTIVE",
            "PENDING_AUTH",
            "CREATED",
            "COMPLETED",
            "TERMINATED",
            "CANCELLED",
            "PAUSED",
            "HALTED",
          ];
          const DONUT_COLORS: Record<string, string> = {
            ACTIVE: "#059669",
            PENDING_AUTH: "#D97706",
            CREATED: "#0284C7",
            COMPLETED: "#1E40AF",
            TERMINATED: "#DC2626",
            CANCELLED: "#475569",
            PAUSED: "#F59E0B",
            HALTED: "#B91C1C",
          };

          const totalCount = liveMandates.length || 1;
          statusBreakdown = DONUT_STATUSES.filter((st) => (counts.get(st) || 0) > 0).map((st) => {
            const val = counts.get(st) || 0;
            return {
              label: st,
              value: val,
              color: DONUT_COLORS[st] || "#64748B",
              percentage: Math.round((val / totalCount) * 100),
            };
          });
        }

        // Top Retailers Performance Table
        let topRetailers = rawDash?.topRetailers;
        if (!Array.isArray(topRetailers) || topRetailers.length === 0) {
          const counts = new Map<string, { total: number; active: number }>();
          liveMandates.forEach((m) => {
            const rId = m.retailerId;
            if (rId) {
              const cur = counts.get(rId) || { total: 0, active: 0 };
              cur.total += 1;
              if (m.status === "ACTIVE") cur.active += 1;
              counts.set(rId, cur);
            }
          });

          topRetailers = liveRetailers
            .map((r) => {
              const c = counts.get(r.id) || {
                total: Number(r.totalMandates || 0),
                active: Number(r.activeMandates || 0),
              };
              const rate = c.total > 0 ? Math.round((c.active / c.total) * 100) : Number(r.successRate || 0);
              return {
                id: r.id,
                name: r.name || "Retailer",
                shopName: r.shopName || r.name || "Shop",
                city: r.city || "—",
                total: c.total,
                active: c.active,
                successRate: rate,
                lastLoginAt: r.lastLoginAt || r.joinedAt || null,
                isActive: r.isActive !== false,
              };
            })
            .sort((a, b) => b.total - a.total)
            .slice(0, 10);
        }

        // Recent Mandates Activity Table
        let recentMandates = rawDash?.recentMandates;
        if (!Array.isArray(recentMandates) || recentMandates.length === 0) {
          recentMandates = liveMandates
            .slice()
            .sort((a, b) => {
              const ta = new Date(a.rawCreatedAt || a.createdAt || 0).getTime();
              const tb = new Date(b.rawCreatedAt || b.createdAt || 0).getTime();
              return tb - ta;
            })
            .slice(0, 10)
            .map((m) => ({
              id: m.id,
              refNo: m.refNo,
              retailerId: m.retailerId,
              shopName: m.shopName || "Shop",
              customerName: m.customerName,
              customerMobile: m.customerMobile,
              amount: Number(m.emiAmount || m.amount || 0),
              frequency: m.frequency || "Monthly",
              status: m.status,
              createdAt: m.rawCreatedAt || m.createdAt || new Date().toISOString(),
            }));
        }

        setDashboardData({
          metrics: {
            totalMandates,
            activeMandates,
            pendingAuthMandates,
            completedMandates,
            failedRejectedMandates,
            failedRejectedPct,
            successRate,
            totalRetailers,
            activeRetailers,
            mandatesToday,
            authorisedToday,
            walletBalance,
            blockedRetailersCount,
            pendingKyc,
            totalMandatesTrend: backendMetrics.totalMandatesTrend !== undefined ? Number(backendMetrics.totalMandatesTrend) : 0,
            activeMandatesTrend: backendMetrics.activeMandatesTrend !== undefined ? Number(backendMetrics.activeMandatesTrend) : 0,
            successRateTrend: backendMetrics.successRateTrend !== undefined ? Number(backendMetrics.successRateTrend) : 0,
          },
          trends: {
            daily: {
              created: dailyCreated,
              activated: dailyActivated,
            },
            monthly: {
              created: monthlyCreated,
              activated: monthlyActivated,
            },
          },
          statusBreakdown,
          topRetailers,
          recentMandates,
        });

        if (isRefresh) {
          toast.success("Dashboard refreshed with latest data");
        }
      } catch (err: any) {
        console.error("Dashboard data load error:", err);
        setError(err?.message || "Failed to load dashboard data");
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [range, customFrom, customTo, toast]
  );

  useEffect(() => {
    fetchDashboard(range, customFrom, customTo, false);
  }, [range, fetchDashboard]);

  const handleRangeSelect = (r: string) => {
    setRangeOpen(false);
    if (r === "Custom Range") {
      setShowCustomModal(true);
    } else {
      setRange(r);
    }
  };

  const handleApplyCustomRange = () => {
    if (!customFrom || !customTo) {
      toast.error("Please select both start and end dates");
      return;
    }
    setRange("Custom Range");
    setShowCustomModal(false);
    fetchDashboard("Custom Range", customFrom, customTo, false);
  };

  const handleExport = () => {
    if (!dashboardData) {
      toast.error("No dashboard data available to export");
      return;
    }

    const m = dashboardData?.metrics || ({} as DashboardMetrics);
    const rows: string[][] = [
      ["=== MANDATEPRO ADMIN DASHBOARD REPORT ==="],
      ["Generated At", new Date().toLocaleString("en-IN")],
      ["Period / Filter", range === "Custom Range" ? `${customFrom} to ${customTo}` : range],
      [""],
      ["=== KEY PERFORMANCE INDICATORS ==="],
      ["Total Mandates", String(m.totalMandates ?? 0)],
      ["Active Mandates", String(m.activeMandates ?? 0)],
      ["Pending Authentication", String(m.pendingAuthMandates ?? 0)],
      ["Success Rate", `${m.successRate ?? "0.0"}%`],
      ["Active Retailers", `${m.activeRetailers ?? 0} of ${m.totalRetailers ?? 0} total`],
      ["Mandates Created Today", String(m.mandatesToday ?? 0)],
      ["Mandates Authorised Today", String(m.authorisedToday ?? 0)],
      ["Terminated / Halted", `${m.failedRejectedMandates ?? 0} (${m.failedRejectedPct ?? "0.0"}%)`],
      ["Total Retailer Wallet Balance", formatINR(m.walletBalance ?? 0)],
      ["Blocked Retailers Count", String(m.blockedRetailersCount ?? 0)],
      ["Pending KYC Verification", String(m.pendingKyc ?? 0)],
      [""],
      ["=== MANDATE STATUS BREAKDOWN ==="],
      ["Status", "Count", "Percentage"],
      ...(dashboardData.statusBreakdown || []).map((s) => [s.label, String(s.value), `${s.percentage}%`]),
      [""],
      ["=== TOP RETAILERS PERFORMANCE ==="],
      ["Retailer ID", "Retailer Name", "Shop/Business Name", "City", "Total Mandates", "Active Mandates", "Success Rate", "Status"],
      ...(dashboardData.topRetailers || []).map((r) => [
        r.id,
        r.name,
        r.shopName,
        r.city,
        String(r.total),
        String(r.active),
        `${r.successRate}%`,
        r.isActive ? "Active" : "Inactive",
      ]),
      [""],
      ["=== RECENT MANDATES ==="],
      ["Ref No", "Retailer", "Customer Name", "Customer Mobile", "Amount", "Frequency", "Status", "Created At"],
      ...(dashboardData.recentMandates || []).map((rm) => [
        rm.refNo,
        rm.shopName,
        rm.customerName,
        rm.customerMobile,
        String(rm.amount),
        rm.frequency,
        rm.status,
        formatDate(rm.createdAt),
      ]),
    ];

    const csvContent = rows
      .map((row) => row.map((val) => `"${String(val ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `MandatePro_Admin_Dashboard_${range.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Dashboard report downloaded successfully");
  };

  const metrics: DashboardMetrics = dashboardData?.metrics || {
    totalMandates: 0,
    activeMandates: 0,
    pendingAuthMandates: 0,
    completedMandates: 0,
    failedRejectedMandates: 0,
    failedRejectedPct: "0.0",
    successRate: "0.0",
    totalRetailers: 0,
    activeRetailers: 0,
    mandatesToday: 0,
    authorisedToday: 0,
    walletBalance: 0,
    blockedRetailersCount: 0,
    pendingKyc: 0,
    totalMandatesTrend: 0,
    activeMandatesTrend: 0,
    successRateTrend: 0,
  };

  // Donut chart segments
  const donutSegments: DonutSegment[] = useMemo(() => {
    if (!dashboardData?.statusBreakdown) return [];
    return dashboardData.statusBreakdown.map((s) => ({
      label: s.label,
      value: s.value,
      color: s.color,
    }));
  }, [dashboardData]);

  // Line chart points
  const activeSeriesA =
    trendMode === "Daily"
      ? dashboardData?.trends?.daily?.created || []
      : dashboardData?.trends?.monthly?.created || [];

  const activeSeriesB =
    trendMode === "Daily"
      ? dashboardData?.trends?.daily?.activated || []
      : dashboardData?.trends?.monthly?.activated || [];

  const topRetailersList = dashboardData?.topRetailers || [];
  const [refreshingMandateId, setRefreshingMandateId] = useState<string | null>(null);

  async function handleRefreshRecentMandate(e: React.MouseEvent, row: DashboardRecentMandate) {
    e.stopPropagation();
    const idToSync = row.refNo || row.id;
    if (!idToSync) return;
    setRefreshingMandateId(row.id);
    try {
      const res = await mandateService.syncWithRocketPay(idToSync);
      const updated = res?.data?.mandate || res?.data;
      if (updated && updated.status) {
        setDashboardData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            recentMandates: prev.recentMandates.map((m) =>
              m.id === row.id || m.refNo === row.refNo ? { ...m, ...updated, status: updated.status } : m
            ),
          };
        });
        toast.success(`Mandate status updated: ${updated.status.replace("_", " ")}`);
      } else {
        loadData(true);
        toast.success(res?.message || "Status refreshed from gateway");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to refresh status from gateway");
    } finally {
      setRefreshingMandateId(null);
    }
  }

  const recentMandatesList = dashboardData?.recentMandates || [];

  const recentColumns: Column<DashboardRecentMandate>[] = [
    {
      key: "refNo",
      header: "Ref No",
      render: (row) => (
        <button
          type="button"
          onClick={() => navigate(`/admin/mandates/${row.refNo}`)}
          className="text-[13px] font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
        >
          {row.refNo}
        </button>
      ),
    },
    {
      key: "shopName",
      header: "Retailer",
      render: (row) => (
        <div>
          <p className="text-sm font-medium text-ink">{row.shopName}</p>
          <p className="text-xs text-ink-secondary">{row.retailerId}</p>
        </div>
      ),
    },
    {
      key: "customerName",
      header: "Customer",
      render: (row) => (
        <div>
          <p className="text-sm font-medium text-ink">{row.customerName}</p>
          <p className="text-xs text-ink-secondary">{row.customerMobile}</p>
        </div>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      render: (row) => (
        <div className="text-right">
          <p className="text-sm font-medium text-ink">{formatINR(row.amount)}</p>
          <p className="text-xs text-ink-secondary">{row.frequency}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => {
        const isSyncing = refreshingMandateId === row.id || refreshingMandateId === row.refNo;
        return (
          <div className="inline-flex items-center gap-1.5">
            <StatusBadge status={row.status} />
            <button
              type="button"
              onClick={(e) => handleRefreshRecentMandate(e, row)}
              disabled={isSyncing}
              title="Fetch latest status from gateway"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-primary-600 hover:border-primary-300 shadow-sm transition-all cursor-pointer disabled:opacity-50"
            >
              <RefreshCw className={`h-3 w-3 ${isSyncing ? "animate-spin text-primary-600" : ""}`} />
            </button>
          </div>
        );
      },
    },
    {
      key: "createdAt",
      header: "Created",
      render: (row) => (
        <div>
          <p className="text-[13px] text-ink">{formatDate(row.createdAt)}</p>
          <p className="text-xs text-ink-secondary">{formatTime(row.createdAt)}</p>
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-ink">Dashboard</h1>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 border border-emerald-200">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live API
            </span>
          </div>
          <p className="mt-1 text-sm text-ink-secondary">Platform-wide mandate activity across all retailers</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Range Selector */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setRangeOpen((v) => !v)}
              className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-slate-300 bg-white px-3.5 text-sm font-medium text-ink hover:bg-slate-50 cursor-pointer shadow-sm transition-colors"
            >
              <Calendar className="h-4 w-4 text-ink-muted" />
              {range}
              <ChevronDown className="h-4 w-4 text-ink-muted" />
            </button>
            {rangeOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setRangeOpen(false)} />
                <div className="absolute right-0 z-20 mt-2 w-48 rounded-lg border border-line bg-white py-1 shadow-dropdown animate-fadeIn">
                  {RANGES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => handleRangeSelect(r)}
                      className={`block w-full whitespace-nowrap px-4 py-2 text-left text-sm cursor-pointer ${
                        range === r ? "bg-primary-50 text-primary-600 font-medium" : "text-ink hover:bg-slate-50"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Refresh Button */}
          <Button
            variant="secondary"
            onClick={() => fetchDashboard(range, customFrom, customTo, true)}
            disabled={loading || refreshing}
            leftIcon={<RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin text-primary-500" : ""}`} />}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>

          {/* Export Button */}
          <Button
            variant="secondary"
            onClick={handleExport}
            disabled={loading || !dashboardData}
            leftIcon={<Download className="h-4 w-4" />}
          >
            Export Report
          </Button>
        </div>
      </div>

      {/* Custom Range Modal */}
      {showCustomModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl animate-fadeIn">
            <h3 className="text-lg font-semibold text-ink">Select Custom Date Range</h3>
            <p className="mt-1 text-sm text-ink-secondary">Choose start and end dates to filter metrics.</p>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-ink-secondary mb-1">From Date</label>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-ink focus:border-primary-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-secondary mb-1">To Date</label>
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-ink focus:border-primary-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setShowCustomModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleApplyCustomRange}>
                Apply Filter
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="mt-6 rounded-lg border border-danger/30 bg-danger-bg p-4 text-danger flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
          <Button size="sm" variant="secondary" onClick={() => fetchDashboard(range, customFrom, customTo, false)}>
            Try Again
          </Button>
        </div>
      )}

      {/* Loading Skeletons */}
      {loading ? (
        <div className="mt-6 flex flex-col gap-5">
          <CardSkeleton cards={4} />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <SkeletonBlock className="h-28 rounded-[10px]" />
            <SkeletonBlock className="h-28 rounded-[10px]" />
            <SkeletonBlock className="h-28 rounded-[10px]" />
            <SkeletonBlock className="h-28 rounded-[10px]" />
            <SkeletonBlock className="h-28 rounded-[10px]" />
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[65%_35%]">
            <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <SkeletonBlock className="h-5 w-40" />
              <SkeletonBlock className="mt-6 h-56 w-full" />
            </div>
            <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <SkeletonBlock className="h-5 w-36" />
              <div className="mt-6 flex justify-center">
                <SkeletonBlock className="h-[180px] w-[180px] rounded-full" />
              </div>
            </div>
          </div>
          <TableSkeleton rows={5} columns={6} />
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-4">
          {/* Top 4 Stat cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Total Mandates"
              value={Number(metrics.totalMandates ?? 0).toLocaleString("en-IN")}
              icon={FileText}
              trend={metrics.totalMandatesTrend}
              onClick={() => navigate("/admin/mandates?tab=All")}
            />
            <StatCard
              label="Active Mandates"
              value={Number(metrics.activeMandates ?? 0).toLocaleString("en-IN")}
              icon={CheckCircle2}
              iconBg="bg-success-bg"
              iconColor="text-success"
              trend={metrics.activeMandatesTrend}
              onClick={() => navigate("/admin/mandates?tab=Active")}
            />
            <button
              type="button"
              onClick={() => navigate("/admin/mandates?tab=Created")}
              className="rounded-[10px] border border-line bg-card p-5 text-left shadow-card hover:border-primary-400 hover:shadow-md hover:-translate-y-1 active:translate-y-0 transition-all cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-1">
                  <span className="text-[13px] text-ink-secondary">Pending Authentication</span>
                  <span className="text-[30px] font-bold leading-tight text-ink">
                    {Number(metrics.pendingAuthMandates ?? 0).toLocaleString("en-IN")}
                  </span>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-warning-bg text-warning">
                  <Clock className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-3 text-xs text-ink-secondary">Awaiting customer action</div>
            </button>
            <StatCard
              label="Success Rate"
              value={`${metrics.successRate ?? "0.0"}%`}
              icon={TrendingUp}
              iconBg="bg-info-bg"
              iconColor="text-info"
              trend={metrics.successRateTrend}
              onClick={() => navigate("/admin/reports")}
            />
          </div>

          {/* Second row of 5 mini stats */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <MiniStat
              label="Active Retailers"
              value={`${metrics.activeRetailers ?? 0}`}
              sub={`of ${metrics.totalRetailers ?? 0} total`}
              onClick={() => navigate("/admin/retailers?status=Active")}
            />
            <MiniStat
              label="Mandates Today"
              value={`${metrics.mandatesToday ?? 0}`}
              sub={`${metrics.authorisedToday ?? 0} authorised so far`}
              onClick={() => navigate("/admin/mandates?range=Today")}
            />
            {/* <MiniStat
              label="Terminated / Halted"
              value={`${metrics.failedRejectedMandates ?? 0}`}
              sub={`${metrics.failedRejectedPct ?? "0.0"}% of total`}
              onClick={() => navigate("/admin/mandates?tab=Terminated")}
            /> */}
            <button
              type="button"
              onClick={() => navigate("/admin/wallets")}
              className="rounded-[10px] border border-line bg-card p-5 text-left shadow-card hover:border-primary-400 hover:shadow-md hover:-translate-y-1 active:translate-y-0 transition-all cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-1">
                  <span className="text-[13px] text-ink-secondary">Wallet Balance</span>
                  <span className="text-2xl font-bold leading-tight text-ink">{formatINR(metrics.walletBalance ?? 0)}</span>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <Wallet className="h-5 w-5" />
                </div>
              </div>
              <p className="mt-3 text-xs text-ink-secondary">System-wide retailer funds</p>
            </button>
            <button
              type="button"
              onClick={() => navigate("/admin/retailers?status=Blocked")}
              className="rounded-[10px] border border-line bg-card p-5 text-left shadow-card hover:border-primary-400 hover:shadow-md hover:-translate-y-1 active:translate-y-0 transition-all cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-1">
                  <span className="text-[13px] text-ink-secondary">Risk / Flagged</span>
                  <span className="text-2xl font-bold leading-tight text-ink">{metrics.blockedRetailersCount ?? 0}</span>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#FEF2F2] text-[#DC2626]">
                  <ShieldAlert className="h-5 w-5" />
                </div>
              </div>
              <p className="mt-3 text-xs text-ink-secondary">
                {metrics.blockedRetailersCount ?? 0} retailer{(metrics.blockedRetailersCount ?? 0) === 1 ? "" : "s"} blocked
              </p>
            </button>
            <button
              type="button"
              onClick={() => navigate("/admin/kyc")}
              className="rounded-[10px] border border-line bg-card p-5 text-left shadow-card hover:border-primary-400 hover:shadow-md hover:-translate-y-1 active:translate-y-0 transition-all cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-1">
                  <span className="text-[13px] text-ink-secondary">Pending KYC</span>
                  <span className="text-2xl font-bold leading-tight text-ink">{metrics.pendingKyc ?? 0}</span>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#FFFBEB] text-[#D97706]">
                  <Clock className="h-5 w-5" />
                </div>
              </div>
              <p className="mt-3 text-xs text-ink-secondary">Awaiting verification</p>
            </button>
          </div>

          {/* Two-column section: Trend Line Chart & Status Donut Chart */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[65%_35%]">
            <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-ink">Mandate Trend</h3>
                  <p className="text-xs text-ink-secondary mt-0.5">Live volume analysis</p>
                </div>
                <div className="flex items-center rounded-full bg-slate-100 p-1">
                  {(["Daily", "Monthly"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setTrendMode(m)}
                      className={`whitespace-nowrap rounded-full px-3 py-1 text-[13px] font-medium cursor-pointer transition-colors ${
                        trendMode === m ? "bg-white text-ink shadow-sm" : "text-ink-secondary hover:text-ink"
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-5">
                <span className="inline-flex items-center gap-1.5 text-[13px] text-ink-secondary">
                  <span className="h-2 w-2 rounded-full bg-[#1E40AF]" />
                  Created
                </span>
                <span className="inline-flex items-center gap-1.5 text-[13px] text-ink-secondary">
                  <span className="h-2 w-2 rounded-full bg-[#059669]" />
                  Activated
                </span>
              </div>
              <div className="mt-3">
                <AdminLineChart seriesA={activeSeriesA} seriesB={activeSeriesB} />
              </div>
            </div>

            <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <h3 className="text-lg font-semibold text-ink">Status Breakdown</h3>
              <p className="text-xs text-ink-secondary mt-0.5">Distribution by mandate lifecycle</p>
              <div className="mt-5 flex justify-center">
                {donutSegments.length > 0 ? (
                  <DonutChart
                    segments={donutSegments}
                    centerValue={Number(metrics.totalMandates ?? 0).toLocaleString("en-IN")}
                    centerLabel="Total"
                  />
                ) : (
                  <div className="flex h-[180px] w-[180px] items-center justify-center rounded-full border border-dashed border-slate-200 text-xs text-ink-muted">
                    No status data
                  </div>
                )}
              </div>
              <div className="mt-5 flex flex-col gap-2.5 max-h-[220px] overflow-y-auto pr-1">
                {(dashboardData?.statusBreakdown || []).map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    onClick={() => {
                      const isPending = s.label === "PENDING_AUTH" || s.label === "PENDING" || s.label === "AUTH_PENDING";
                      const target = isPending ? "Created" : (STATUS_META[s.label]?.label || s.label);
                      navigate(`/admin/mandates?tab=${target}`);
                    }}
                    className="flex w-full items-center justify-between py-1 px-2 -mx-2 rounded-md hover:bg-slate-50 transition-colors text-left cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                      <span className="text-[13px] text-ink">{STATUS_META[s.label]?.label || s.label}</span>
                    </div>
                    <span className="text-[13px] font-medium text-ink-secondary">
                      {Number(s.value ?? 0).toLocaleString("en-IN")} · {s.percentage}%
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Retailer Performance Table */}
          <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-ink">Retailer Performance</h3>
                <p className="text-[13px] text-ink-secondary">Top retailers ranked by live mandate volume</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin/retailers")}
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                View All Retailers
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left">
                <thead>
                  <tr className="border-b border-line text-xs font-semibold uppercase tracking-wide text-ink-secondary">
                    <th className="px-4 py-3">Retailer</th>
                    <th className="px-4 py-3 text-right">Total Mandates</th>
                    <th className="px-4 py-3 text-right">Active</th>
                    <th className="px-4 py-3">Success Rate</th>
                    <th className="px-4 py-3">Last Active</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {topRetailersList.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-sm text-ink-secondary">
                        No retailer activity recorded yet.
                      </td>
                    </tr>
                  ) : (
                    topRetailersList.map((r) => (
                      <tr
                        key={r.id}
                        onClick={() => navigate("/admin/retailers")}
                        className="h-14 cursor-pointer border-b border-line last:border-0 hover:bg-page transition-colors"
                      >
                        <td className="px-4">
                          <div className="flex items-center gap-3">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[13px] font-semibold text-primary-500">
                              {initials(r.name)}
                            </span>
                            <div>
                              <p className="text-sm font-medium text-ink">{r.shopName}</p>
                              <p className="text-xs text-ink-secondary">
                                {r.name} · {r.city}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 text-right text-sm font-medium text-ink">{r.total ?? 0}</td>
                        <td className="px-4 text-right text-sm text-ink">{r.active ?? 0}</td>
                        <td className="px-4">
                          <p className="text-sm font-medium text-ink">{r.successRate ?? 0}%</p>
                          <div className="mt-1 h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${Math.min(100, Math.max(0, Number(r.successRate) || 0))}%`,
                                backgroundColor: successBarColor(Number(r.successRate) || 0),
                              }}
                            />
                          </div>
                        </td>
                        <td className="px-4 text-[13px] text-ink-secondary">{relativeTime(r.lastLoginAt)}</td>
                        <td className="px-4">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              r.isActive ? "bg-success-bg text-emerald-700" : "bg-[#F1F5F9] text-slate-600"
                            }`}
                          >
                            {r.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Activity Table */}
          <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-ink">Recent Activity</h3>
                <p className="text-[13px] text-ink-secondary">Latest mandates created across all retailers</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate("/admin/mandates")}>
                View All
              </Button>
            </div>
            <DataTable
              columns={recentColumns}
              data={recentMandatesList}
              rowKey={(row) => row.id || row.refNo}
              onRowClick={(row) => navigate(`/admin/mandates/${row.refNo}`)}
              pageSize={10}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({
  label,
  value,
  sub,
  onClick,
}: {
  label: string;
  value: string;
  sub: string;
  onClick?: () => void;
}) {
  const isClickable = !!onClick;
  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`rounded-[10px] border border-line bg-card p-5 shadow-card transition-all ${
        isClickable
          ? "cursor-pointer hover:border-primary-400 hover:shadow-md hover:-translate-y-1 active:translate-y-0"
          : ""
      }`}
    >
      <p className="text-[13px] text-ink-secondary">{label}</p>
      <p className="mt-1 text-2xl font-bold text-ink">{value}</p>
      <p className="mt-1 text-xs text-ink-secondary">{sub}</p>
    </div>
  );
}